import { useMemo } from "react";
import useSWR from "swr";
import fetcher from "@adora/shared/utils/fetch";
import { RelativeUrl } from "@adora/shared/utils/environment";
import { Brand, BrandServerResponse, BrandStatus } from "@adora/shared/types/brand";
import { snakeToCamel } from "@adora/shared/utils/case-conversions";
import post from "@adora/shared/utils/post";
import patch from "@adora/shared/utils/patch";

export const BrandsUrl = "brands";
export const BrandsSwrKey = `${RelativeUrl}/${BrandsUrl}`;

export const transformBrand = (brand: BrandServerResponse[]): Brand[] => {
  return brand.map<Brand>((b) => {
    const transformed = snakeToCamel(b) as Omit<Brand, "creationTime"> & { creationTime: string | null };
    return {
      ...transformed,
      creationTime: transformed.creationTime ? new Date(transformed.creationTime) : null
    };
  });
};

export interface CreateBrandOptions {
  name: string;
  description?: string;
  s3Bucket?: string;
  status?: BrandStatus;
}

const useBrands = () => {
  const { data, error, isLoading, mutate } = useSWR(
    BrandsSwrKey,
    (url: string) => fetcher<BrandServerResponse[]>(url),
    {}
  );

  const brands = useMemo(() => (data ? transformBrand(data) : undefined), [data]);

  async function createBrand({ name, description, s3Bucket, status }: CreateBrandOptions) {
    const newBrandResult = await post<BrandServerResponse>(
      `${RelativeUrl}/${BrandsUrl}`,
      {},
      {
        name,
        ...(description && { description }),
        ...(s3Bucket && { s3_bucket: s3Bucket }),
        ...(status && { status })
      }
    );

    newBrandResult.match(
      (e) => console.error(e.message),
      (newBrand) => {
        mutate((prevBrands) => (prevBrands ? [...prevBrands, newBrand] : [newBrand]), false);
      }
    );
  }

  async function updateBrand(id: number, { name, description, s3Bucket, status }: CreateBrandOptions) {
    const updatedBrandResult = await patch<BrandServerResponse>(
      `${RelativeUrl}/${BrandsUrl}/${id}`,
      {},
      {
        ...(name && { name }),
        ...(description && { description }),
        ...(s3Bucket && { s3_bucket: s3Bucket }),
        ...(status && { status })
      }
    );

    updatedBrandResult.match(
      (e) => console.error(e.message),
      (updatedBrand) =>
        mutate(
          (prevBrands) => prevBrands?.map((brand) => (brand.id === id ? updatedBrand : brand)) ?? [updatedBrand],
          false
        )
    );
  }

  return {
    brands,
    error,
    isLoading,
    createBrand,
    updateBrand
  };
};

export default useBrands;
